import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AlertTriangle, CheckCircle, XCircle, Clock, Eye, User, Building2 } from 'lucide-react';
import { useOrganization } from '../../contexts/OrganizationContext';
import AlertComparison from './AlertComparison';
import { $api } from '../../api';
import type { paths } from '../../api/paths';
import { Client } from '../../types';
import ErrorMessage from '../ErrorMessage';

// Extract alert type from API definitions
type AlertFromAPI = paths['/api/organization/{organization_id}/alerts']['get']['responses']['200']['content']['application/json']['alerts'][0];

const Alerts: React.FC = () => {
  const navigate = useNavigate();
  const { currentOrganization } = useOrganization();
  const orgId = currentOrganization?.id || '';
  const [activeTab, setActiveTab] = useState<'pending' | 'resolved'>('pending');
  const [selectedAlert, setSelectedAlert] = useState<AlertFromAPI | null>(null);
  const [selectedEntity, setSelectedEntity] = useState<Client | null>(null);
  const [showComparison, setShowComparison] = useState(false);

  // Fetch pending alerts
  const pendingAlertsQuery = $api.useQuery(
    'get',
    '/api/organization/{organization_id}/alerts',
    {
      params: {
        path: {
          organization_id: orgId,
        },
        query: {
          resolution_type: 'pending',
        },
      },
    },
    {
      enabled: !!orgId,
    }
  );

  // Fetch all alerts (we'll filter resolved ones client-side)
  // Note: The API doesn't support fetching "all resolved" directly, so we fetch all and filter
  const allAlertsQuery = $api.useQuery(
    'get',
    '/api/organization/{organization_id}/alerts',
    {
      params: {
        path: {
          organization_id: orgId,
        },
        query: {},
      },
    },
    {
      enabled: !!orgId,
    }
  );

  // Fetch entity details when an alert is selected
  const entityQuery = $api.useQuery(
    'get',
    '/api/organization/{organization_id}/entities/{id}',
    {
      params: {
        path: {
          organization_id: orgId,
          id: selectedAlert?.entity_id || '',
        },
      },
    },
    {
      enabled: !!orgId && !!selectedAlert?.entity_id,
    }
  );

  const pendingAlerts = pendingAlertsQuery.data?.alerts || [];
  const allAlerts = allAlertsQuery.data?.alerts || [];
  const resolvedAlerts = allAlerts.filter(alert => alert.resolution_type !== undefined);

  const currentAlerts = activeTab === 'pending' ? pendingAlerts : resolvedAlerts;

  // Update selected entity when entity query completes
  React.useEffect(() => {
    if (entityQuery.data && selectedAlert) {
      const entityData = entityQuery.data;
      const entity: Client = {
        id: entityData.id,
        name: entityData.name,
        email: entityData.email || '',
        phone: entityData.phone || '',
        company: entityData.kind === 'company' ? entityData.name : '',
        status: 'verified',
        kycStatus: 'completed',
        createdAt: entityData.created_at,
        lastUpdated: entityData.updated_at,
        riskScore: entityData.risk_level === 'high' ? 80 : entityData.risk_level === 'mid' ? 50 : 20,
        documents: [],
      };
      setSelectedEntity(entity);
    }
  }, [entityQuery.data, selectedAlert]);

  const getAlertTypeLabel = (type: string) => {
    switch (type) {
      case 'sanctioned':
      case 'sanction': return 'Sanction';
      case 'pep': return 'PEP';
      case 'adverse_media': return 'Médias adverses';
      default: return type;
    }
  };

  const getAlertTypeColor = (type: string) => {
    switch (type) {
      case 'sanctioned':
      case 'sanction': return 'bg-red-100 text-red-800';
      case 'pep': return 'bg-yellow-100 text-yellow-800';
      case 'adverse_media': return 'bg-orange-100 text-orange-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getSeverityColor = (severity: 'low' | 'mid' | 'high') => {
    switch (severity) {
      case 'high': return 'text-red-600';
      case 'mid': return 'text-yellow-600';
      case 'low': return 'text-green-600';
      default: return 'text-gray-600';
    }
  };

  const getSeverityIcon = (severity: 'low' | 'mid' | 'high') => {
    switch (severity) {
      case 'high': return <XCircle className="h-4 w-4" />;
      case 'mid': return <AlertTriangle className="h-4 w-4" />;
      case 'low': return <CheckCircle className="h-4 w-4" />;
      default: return <Clock className="h-4 w-4" />;
    }
  };

  const getSeverityLabel = (severity: 'low' | 'mid' | 'high') => {
    switch (severity) {
      case 'high': return 'Élevé';
      case 'mid': return 'Moyen';
      case 'low': return 'Faible';
      default: return severity;
    }
  };

  const handleAlertClick = async (alert: AlertFromAPI) => {
    setSelectedAlert(alert);
    setShowComparison(true);
  };

  const handleResolveAlert = (alertId: string, isMatch: boolean, reason?: string) => {
    console.log('Résolution alerte:', { alertId, isMatch, reason });
    setShowComparison(false);
    setSelectedAlert(null);
    setSelectedEntity(null);
    // Refetch alerts to update the list
    pendingAlertsQuery.refetch();
    allAlertsQuery.refetch();
  };

  const handleEntityClick = (entityId: string) => {
    if (currentOrganization) {
      navigate(`/${currentOrganization.id}/entity/${entityId}`);
    }
  };

  const isLoading = activeTab === 'pending' ? pendingAlertsQuery.isLoading : allAlertsQuery.isLoading;
  const error = activeTab === 'pending' ? pendingAlertsQuery.error : allAlertsQuery.error;

  if (isLoading) {
    return (
      <div className="p-6">
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          <span className="ml-3 text-gray-600">Chargement des alertes...</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6">
        <ErrorMessage error={error} />
      </div>
    );
  }

  return (
    <>
      <div className="p-6">
      {/* Header */}
      <div className="flex justify-between items-center mb-6">
        <div>
          <h2 className="text-xl font-semibold text-gray-900">Alertes</h2>
          <p className="text-gray-600 mt-1">
            {pendingAlerts.length} alerte{pendingAlerts.length !== 1 ? 's' : ''} en attente
          </p>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex space-x-1 mb-6">
        <button
          onClick={() => setActiveTab('pending')}
          className={`flex items-center space-x-2 px-4 py-2 rounded-lg font-medium transition-colors ${
            activeTab === 'pending'
              ? 'bg-red-600 text-white'
              : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
          }`}
        >
          <AlertTriangle className="h-4 w-4" />
          <span>En attente ({pendingAlerts.length})</span>
        </button>
        <button
          onClick={() => setActiveTab('resolved')}
          className={`flex items-center space-x-2 px-4 py-2 rounded-lg font-medium transition-colors ${
            activeTab === 'resolved'
              ? 'bg-green-600 text-white'
              : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
          }`}
        >
          <CheckCircle className="h-4 w-4" />
          <span>Historique ({resolvedAlerts.length})</span>
        </button>
      </div>

      {/* Alerts List */}
      {currentAlerts.length === 0 ? (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8">
          <div className="text-center">
            <CheckCircle className="h-12 w-12 text-green-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              {activeTab === 'pending' ? 'Aucune alerte en attente' : 'Aucun historique'}
            </h3>
            <p className="text-gray-500">
              {activeTab === 'pending' 
                ? 'Toutes les entités sont conformes'
                : 'Aucune alerte n\'a encore été traitée'
              }
            </p>
          </div>
        </div>
      ) : (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Entité
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Type d'alerte
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Sévérité
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Description
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Date
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {currentAlerts.map((alert) => (
                <tr key={alert.id} className="hover:bg-gray-50 transition-colors">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center space-x-3">
                      <div className="h-8 w-8 bg-blue-100 rounded-full flex items-center justify-center">
                        {alert.entity_type === 'company' ? (
                          <Building2 className="h-4 w-4 text-blue-600" />
                        ) : (
                          <User className="h-4 w-4 text-blue-600" />
                        )}
                      </div>
                      <div>
                        <button
                          onClick={() => handleEntityClick(alert.entity_id)}
                          className="text-sm font-medium text-gray-900 hover:text-blue-600 transition-colors"
                        >
                          {alert.entity_name}
                        </button>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${getAlertTypeColor(alert.alert_type)}`}>
                      {getAlertTypeLabel(alert.alert_type)}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className={`flex items-center space-x-1 ${getSeverityColor(alert.severity)}`}>
                      {getSeverityIcon(alert.severity)}
                      <span className="text-sm font-medium">{getSeverityLabel(alert.severity)}</span>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <p className="text-sm text-gray-900 max-w-xs truncate">{alert.description}</p>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    <div>
                      <p>Créée: {new Date(alert.date || alert.created_at).toLocaleDateString('fr-FR')}</p>
                      {alert.resolution_date && (
                        <p className={alert.resolution_type === 'match' ? 'text-red-600' : 'text-green-600'}>
                          Résolue: {new Date(alert.resolution_date).toLocaleDateString('fr-FR')} ({alert.resolution_type === 'match' ? 'Correspondance' : 'Fausse alerte'})
                        </p>
                      )}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                    <div className="flex items-center justify-end space-x-2">
                      <button
                        onClick={() => handleEntityClick(alert.entity_id)}
                        className="text-gray-600 hover:text-gray-900 transition-colors p-1"
                        title="Voir l'entité"
                      >
                        <Eye className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => handleAlertClick(alert)}
                        className="bg-blue-600 text-white px-3 py-1 rounded text-sm hover:bg-blue-700 transition-colors"
                        title="Analyser l'alerte"
                      >
                        Analyser
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      </div>

      {/* Alert Comparison Modal */}
      {showComparison && selectedAlert && (
        entityQuery.isLoading ? (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg shadow-xl p-6">
              <div className="flex items-center space-x-3">
                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
                <span className="text-gray-600">Chargement des détails de l'entité...</span>
              </div>
            </div>
          </div>
        ) : selectedEntity ? (
          <AlertComparison
            alert={selectedAlert}
            entity={selectedEntity}
            onClose={() => {
              setShowComparison(false);
              setSelectedAlert(null);
              setSelectedEntity(null);
            }}
            onResolve={handleResolveAlert}
          />
        ) : null
      )}
    </>
  );
};

export default Alerts;